import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, GripVertical, Edit, Trash2, Save, X, Upload, Plus, Image as ImageIcon, Clock, Sparkles, Layers, CheckSquare, Square, Lock, Unlock, ListOrdered, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import toast from 'react-hot-toast';
import { authFetch } from '../utils/authUtils';
import { resolveCoverUrl } from '../utils/coverUrl';
import PriceTagEditor from '../components/PriceTagEditor';
import { SortableSeriesRow, SortableEpisodeRow } from '../components/SortableContentRows';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ManageContent = () => {
  const navigate = useNavigate();
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSeries, setExpandedSeries] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [coverPreview, setCoverPreview] = useState(null);
  const [newCoverFile, setNewCoverFile] = useState(null);
  const [uploadingEpisode, setUploadingEpisode] = useState(null);
  const [episodeForm, setEpisodeForm] = useState({
    title: '',
    description: '',
    episode_number: ''
  });
  const [episodeFile, setEpisodeFile] = useState(null);
  // When true, the upload posts shift_existing=true so the backend bumps
  // any episode at-or-after the chosen number up by 1. Set automatically
  // when the scribe taps a "+ Insert here" row between two episodes.
  const [shiftExisting, setShiftExisting] = useState(false);
  // Tracks which series is currently being renumbered so the button shows
  // a spinner and stays disabled mid-flight.
  const [renumberingId, setRenumberingId] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [aiCoverBusyId, setAiCoverBusyId] = useState('');
  // Bulk-select state — admins can multi-pick series rows and apply
  // delete or vault-toggle actions in one shot. `selectMode` gates the
  // checkboxes + action bar so the page stays clean when not in use.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const exitSelectMode = () => { setSelectMode(false); clearSelection(); };
  // When checked inside the edit form, the chosen cover is also pushed to
  // every existing episode in the series AND set as the series default so
  // future uploaded episodes auto-inherit it. Defaults to ON because that's
  // what users almost always want when they're updating the album art —
  // they expect every episode to use the new image.
  const [applyCoverToAllEpisodes, setApplyCoverToAllEpisodes] = useState(true);

  // Per-upload XP map — `content_id → xp_rewarded`. Fetched once on mount
  // and refreshed after any upload/delete. Visible to admins viewing
  // anyone's content, and scribes viewing their own (the endpoint
  // enforces this — see /api/writer/content-xp). May 31 2026 user spec.
  const [xpMap, setXpMap] = useState({});
  const [xpTotal, setXpTotal] = useState(0);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await authFetch(`${BACKEND_URL}/api/writer/content-xp`);
        if (!res.ok) return;
        const data = await res.json();
        if (!alive) return;
        setXpMap(data.items || {});
        setXpTotal(data.total || 0);
      } catch {
        /* non-fatal */
      }
    })();
    return () => { alive = false; };
  }, []);

  /**
   * Run a bulk action across every selected series. We do them
   * sequentially so a flaky network doesn't fire 50 parallel requests
   * that would rate-limit, and so we can show accurate progress in the
   * toast. Failures are tallied but don't abort the rest of the batch —
   * users get a final summary like "8 deleted, 1 failed".
   */
  const runBulkAction = async (action) => {
    if (bulkBusy) return;
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const labelMap = {
      delete: `Delete ${ids.length} ${ids.length === 1 ? 'series' : 'series'} permanently? This removes every episode + cover too.`,
      vault: `Move ${ids.length} series to the Secret Vault?`,
      library: `Move ${ids.length} series back to the Library?`,
    };
    if (!window.confirm(labelMap[action])) return;
    setBulkBusy(true);
    const tid = toast.loading(`Working on 0 / ${ids.length}…`);
    let ok = 0;
    let fail = 0;
    for (let i = 0; i < ids.length; i += 1) {
      const id = ids[i];
      const item = series.find((s) => s.series_id === id);
      if (!item) { fail += 1; continue; }
      try {
        if (action === 'delete') {
          const ep = item.type === 'audiobook'
            ? `/api/audiobook/series/${id}`
            : `/api/cinematic/series/${id}`;
          const r = await authFetch(`${BACKEND_URL}${ep}`, { method: 'DELETE' });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
        } else {
          const target = action === 'vault';
          const r = await authFetch(
            `${BACKEND_URL}/api/admin/vault/content/${encodeURIComponent(id)}`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ is_vault: target }),
            },
          );
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
        }
        ok += 1;
      } catch {
        fail += 1;
      }
      toast.loading(`Working on ${i + 1} / ${ids.length}…`, { id: tid });
    }
    if (fail === 0) {
      toast.success(`✓ ${ok} ${ok === 1 ? 'series' : 'series'} updated`, { id: tid });
    } else {
      toast.error(`Done — ${ok} ok, ${fail} failed`, { id: tid });
    }
    exitSelectMode();
    fetchAllSeries();
    setBulkBusy(false);
  };

  const generateAiCover = async (item, isRegenerate = false) => {
    if (aiCoverBusyId) return;
    if (isRegenerate && !window.confirm(
      `Regenerate the AI cover for "${item.title}"?\n\n` +
      `The current cover will be replaced. This uses ~$0.04 of AI image credit.`
    )) return;
    // Map our front-end `type` field onto the backend collection name.
    const typeToCol = {
      audiobook: 'audiobook_series',
      cinematic: 'cinematic_series',
    };
    const collection = typeToCol[item.type];
    if (!collection) {
      toast.error(`AI cover for "${item.type}" series not supported yet.`);
      return;
    }
    setAiCoverBusyId(item.series_id);
    const t = toast.loading(isRegenerate
      ? '🔄 Regenerating cover with Nano Banana...'
      : '🎨 Generating cover with Nano Banana...');
    try {
      const res = await authFetch(
        `${BACKEND_URL}/api/admin/ai-cover/${collection}/${item.series_id}`,
        { method: 'POST' },
      );
      const d = await res.json();
      if (!res.ok || d.ok === false) {
        toast.error(d.detail || 'AI cover failed', { id: t });
        return;
      }
      if (d.fallback_used) {
        toast.success('Used gradient fallback (Nano Banana was busy). Cover saved.', { id: t });
      } else {
        toast.success(isRegenerate ? '✨ Cover regenerated!' : '✨ AI cover generated!', { id: t });
      }
      fetchAllSeries();
    } catch (err) {
      toast.error(`AI cover error: ${err.message}`, { id: t });
    } finally {
      setAiCoverBusyId('');
    }
  };

  const runBackfill = async () => {
    if (backfilling) return;
    setBackfilling(true);
    const t = toast.loading('Backfilling durations from disk...');
    try {
      const res = await authFetch(`${BACKEND_URL}/api/admin/backfill-episode-durations`, {
        method: 'POST',
      });
      const d = await res.json();
      if (!res.ok || d.ok === false) {
        toast.error(d.detail || d.error || 'Backfill failed', { id: t });
        return;
      }
      const ci = d.cinematic || {};
      const au = d.audiobook || {};
      toast.success(
        `✅ Cinematic: ${ci.updated || 0} updated · Audiobook: ${au.updated || 0} updated`,
        { id: t, duration: 5000 },
      );
      fetchAllSeries();
    } catch (err) {
      toast.error(`Backfill error: ${err.message}`, { id: t });
    } finally {
      setBackfilling(false);
    }
  };

  useEffect(() => {
    fetchAllSeries();
  }, []);

  const fetchAllSeries = async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/api/library`);
      const data = await response.json();
      setSeries(data.series || []);
    } catch (error) {
      console.error('Failed to fetch series:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (seriesId) => {
    setExpandedSeries(prev => ({
      ...prev,
      [seriesId]: !prev[seriesId]
    }));
  };

  const startEdit = (item) => {
    setEditingId(item.series_id);
    setEditForm({
      title: item.title || '',
      description: item.description || '',
      author: item.author || '',
      genre: item.genre || '',
      difficulty: item.difficulty || '',
      cover_image: item.cover_image || ''
    });
    setCoverPreview(item.cover_image || null);
    setNewCoverFile(null);
  };

  // ─── Per-episode quick edit (title only — covers stay protected
  // by the EditModal upgrade in this same session). Prompts for new
  // title and PUTs to the episode/chapter endpoint.
  const editEpisodeTitle = async (item, ep) => {
    const isAudiobook = item.content_type === 'audiobook' || item.audiobook_url || item.chapters;
    const epId = ep.episode_id || ep.chapter_id;
    if (!epId) return;
    const nextTitle = window.prompt(`Rename episode:\n\nCurrent title:`, ep.title || '');
    if (nextTitle == null) return;
    const trimmed = nextTitle.trim();
    if (!trimmed || trimmed === (ep.title || '').trim()) return;
    const url = isAudiobook
      ? `${BACKEND_URL}/api/audiobook/chapter/${epId}`
      : `${BACKEND_URL}/api/cinematic/episode/${epId}`;
    const tid = toast.loading('Saving title…');
    try {
      const r = await authFetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: trimmed }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('✓ Title updated', { id: tid });
      await fetchAllSeries();
    } catch (e) {
      toast.error(`Failed to save: ${e?.message || 'unknown'}`, { id: tid });
    }
  };

  // Per-episode cover editor — opens a hidden file input, uploads the
  // selected image to the episode/chapter PUT endpoint as a multipart
  // form, then refetches. Backend mirrors to persistent storage.
  const changeEpisodeCover = async (item, ep) => {
    const isAudiobook = item.content_type === 'audiobook' || item.audiobook_url || item.chapters;
    const epId = ep.episode_id || ep.chapter_id;
    if (!epId) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/jpg,image/webp,image/gif';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Cover too large (max 10MB)');
        return;
      }
      const url = isAudiobook
        ? `${BACKEND_URL}/api/audiobook/chapter/${epId}`
        : `${BACKEND_URL}/api/cinematic/episode/${epId}`;
      const formData = new FormData();
      formData.append('cover_image', file);
      const tid = toast.loading('Uploading cover…');
      try {
        const r = await authFetch(url, { method: 'PUT', body: formData });
        if (!r.ok) {
          const detail = await r.text();
          throw new Error(detail || `HTTP ${r.status}`);
        }
        toast.success('✓ Cover updated', { id: tid });
        await fetchAllSeries();
      } catch (e) {
        toast.error(`Failed to upload cover: ${e?.message || 'unknown'}`, { id: tid });
      }
    };
    input.click();
  };

  // ─── Per-episode delete with confirmation.
  const deleteEpisode = async (item, ep) => {
    const isAudiobook = item.content_type === 'audiobook' || item.audiobook_url || item.chapters;
    const epId = ep.episode_id || ep.chapter_id;
    if (!epId) return;
    const confirmed = window.confirm(
      `Delete "${ep.title}"?\n\nThis cannot be undone. Later episodes will keep their current numbers — use Renumber 1→N to close any gap.`,
    );
    if (!confirmed) return;
    const url = isAudiobook
      ? `${BACKEND_URL}/api/audiobook/chapter/${epId}`
      : `${BACKEND_URL}/api/cinematic/episode/${epId}`;
    const tid = toast.loading('Deleting episode…');
    try {
      const r = await authFetch(url, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('✓ Episode deleted', { id: tid });
      await fetchAllSeries();
    } catch (e) {
      toast.error(`Failed to delete: ${e?.message || 'unknown'}`, { id: tid });
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
    setCoverPreview(null);
    setNewCoverFile(null);
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setNewCoverFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setCoverPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const saveEdit = async (seriesId, contentType) => {
    try {
      const endpoint = contentType === 'audiobook' 
        ? `/api/audiobook/series/${seriesId}`
        : `/api/cinematic/series/${seriesId}`;

      let response;

      if (newCoverFile) {
        const formData = new FormData();
        formData.append('cover_image', newCoverFile);
        Object.keys(editForm).forEach(key => {
          // We send `difficulty` even when empty so users can clear it; the
          // backend coerces "" → None.
          if (editForm[key] || key === 'difficulty') formData.append(key, editForm[key] ?? '');
        });

        response = await authFetch(`${BACKEND_URL}${endpoint}`, {
          method: 'PUT',
          body: formData
        });
      } else {
        response = await authFetch(`${BACKEND_URL}${endpoint}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editForm)
        });
      }

      if (!response.ok) throw new Error('Failed to save');

      // If the admin uploaded a new cover AND opted to push it to every
      // episode, fan it out via the sync endpoint. We do this in a second
      // request (after the series PUT succeeds) so the series PUT's cover
      // resolution is the source of truth that gets propagated.
      let syncedCount = 0;
      const isSeries = contentType === 'audiobook' || contentType === 'cinematic';
      if (newCoverFile && applyCoverToAllEpisodes && isSeries) {
        try {
          const syncFd = new FormData();
          syncFd.append('cover_image', newCoverFile);
          syncFd.append('series_type', contentType);
          const syncRes = await authFetch(
            `${BACKEND_URL}/api/admin/sync-series-cover/${seriesId}`,
            { method: 'POST', body: syncFd },
          );
          if (syncRes.ok) {
            const sd = await syncRes.json();
            syncedCount = sd.episodes_updated || 0;
          }
        } catch (err) {
          // Non-fatal — series itself is updated; just warn.
          toast.error(`Series saved but episode-cover sync failed: ${err.message}`);
        }
      }

      if (syncedCount > 0) {
        toast.success(`✨ Series saved & cover applied to ${syncedCount} episodes (and any future ones)`);
      } else {
        toast.success('Series updated successfully');
      }
      cancelEdit();
      fetchAllSeries();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save changes');
    }
  };

  const deleteSeries = async (seriesId, title, contentType) => {
    if (!window.confirm(`Delete "${title}" and ALL its episodes? This cannot be undone.`)) return;

    try {
      const endpoint = contentType === 'audiobook'
        ? `/api/audiobook/series/${seriesId}`
        : `/api/cinematic/series/${seriesId}`;

      const response = await authFetch(`${BACKEND_URL}${endpoint}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to delete');

      toast.success(`${title} deleted successfully`);
      fetchAllSeries();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete series');
    }
  };

  const moveUp = (index) => {
    if (index === 0) return;
    const newSeries = [...series];
    [newSeries[index - 1], newSeries[index]] = [newSeries[index], newSeries[index - 1]];
    setSeries(newSeries);
    saveOrder(newSeries);
  };

  const moveDown = (index) => {
    if (index === series.length - 1) return;
    const newSeries = [...series];
    [newSeries[index], newSeries[index + 1]] = [newSeries[index + 1], newSeries[index]];
    setSeries(newSeries);
    saveOrder(newSeries);
  };

  const saveOrder = async (orderedSeries) => {
    try {
      const cinematicItems = orderedSeries.filter(s => s.type === 'cinematic');
      const audiobookItems = orderedSeries.filter(s => s.type === 'audiobook');

      if (cinematicItems.length > 0) {
        await authFetch(`${BACKEND_URL}/api/cinematic/series/reorder`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: cinematicItems.map((s, i) => ({ id: s.series_id, order: i }))
          })
        });
      }

      if (audiobookItems.length > 0) {
        await authFetch(`${BACKEND_URL}/api/audiobook/series/reorder`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: audiobookItems.map((s, i) => ({ id: s.series_id, order: i }))
          })
        });
      }

      toast.success('Order saved');
    } catch (error) {
      console.error('Failed to save order:', error);
      toast.error('Failed to save order');
    }
  };

  // Compute the lowest unused integer >= 1 for a series. Used to
  // pre-fill "Episode number" with the next gap (or n+1) when opening
  // the Add form so scribes don't have to count manually.
  const nextEpisodeNumber = (seriesItem) => {
    const used = new Set(
      (seriesItem?.episodes || [])
        .map((e) => Number(e.episode_number ?? e.chapter_number))
        .filter((n) => Number.isFinite(n))
    );
    let candidate = 1;
    while (used.has(candidate)) candidate += 1;
    return candidate;
  };

  // Open the Add form pre-filled with the lowest available number.
  // `insertAt` (optional) overrides with a specific position and flips
  // the `shiftExisting` flag so the upload bumps later episodes up.
  const startAddEpisode = (seriesId, insertAt = null) => {
    const seriesItem = series.find((s) => s.series_id === seriesId);
    const number = insertAt != null ? insertAt : nextEpisodeNumber(seriesItem);
    setUploadingEpisode(seriesId);
    setEpisodeForm({
      title: '',
      description: '',
      episode_number: String(number),
    });
    setShiftExisting(insertAt != null);
    setEpisodeFile(null);
  };

  const cancelAddEpisode = () => {
    setUploadingEpisode(null);
    setEpisodeForm({
      title: '',
      description: '',
      episode_number: ''
    });
    setShiftExisting(false);
    setEpisodeFile(null);
  };

  // Re-assign episodes to sequential numbers 1..N based on their current
  // order. Closes any gaps (e.g. #1, #3, #4 → #1, #2, #3) so the player's
  // sequence is correct. Uses the new /renumber endpoint per content type.
  const handleRenumber = async (item) => {
    const isAudiobook = item.content_type === 'audiobook' || item.audiobook_url || item.chapters;
    const url = isAudiobook
      ? `${process.env.REACT_APP_BACKEND_URL}/api/audiobook/series/${item.series_id}/chapters/renumber`
      : `${process.env.REACT_APP_BACKEND_URL}/api/cinematic/series/${item.series_id}/episodes/renumber`;
    if (!window.confirm(
      `Re-assign all ${item.episodes?.length || 0} ${isAudiobook ? 'chapters' : 'episodes'} to sequential numbers 1, 2, 3…?\n\nThis closes any gaps in the sequence.`
    )) return;
    setRenumberingId(item.series_id);
    const tid = toast.loading('Renumbering…');
    try {
      const res = await authFetch(url, { method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      toast.success(`✓ Renumbered ${data.renumbered} ${isAudiobook ? 'chapters' : 'episodes'}`, { id: tid });
      fetchAllSeries();
    } catch (e) {
      toast.error(`Renumber failed: ${e?.message || 'unknown'}`, { id: tid });
    } finally {
      setRenumberingId(null);
    }
  };

  // ─── Episode reorder powered by @dnd-kit ─────────────────────────────
  // Two sensors keep us covered across input devices:
  //   • PointerSensor (mouse/pen/touch) — drag starts after 8 px of
  //     movement so a tap still selects text in nested inputs.
  //   • TouchSensor — long-press (180 ms / 5 px tolerance) on phones
  //     so a quick scroll-flick isn't misread as a drag.
  // Keyboard sensor is included for a11y (Space picks up, arrows move).
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Drag-and-drop reorder for the top-level series list. Mirrors the
  // episode flow: arrayMove → optimistic local state → existing saveOrder()
  // call which splits the items by type and PUTs the per-type reorder
  // endpoints. Cross-type ordering is best-effort (backend stores order
  // per type), matching the existing moveUp/moveDown convention.
  const onSeriesDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = series.findIndex((s) => s.series_id === active.id);
    const newIdx = series.findIndex((s) => s.series_id === over.id);
    if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx) return;
    const next = arrayMove(series, oldIdx, newIdx);
    setSeries(next);
    try { navigator.vibrate?.(12); } catch (_) { /* ignore */ }
    saveOrder(next);
  };

  // Called by @dnd-kit when the user drops a sortable row. We compute
  // the new array, optimistically update local state so the UI doesn't
  // flicker, then push to the backend via the existing two-step
  // persistEpisodeOrder() (reorder → renumber).
  const onEpisodeDragEnd = (item) => (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = item.episodes.findIndex(
      (ep) => (ep.episode_id || ep.chapter_id) === active.id,
    );
    const newIdx = item.episodes.findIndex(
      (ep) => (ep.episode_id || ep.chapter_id) === over.id,
    );
    if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx) return;
    const next = arrayMove(item.episodes, oldIdx, newIdx);
    setSeries((prev) =>
      prev.map((s) => (s.series_id === item.series_id ? { ...s, episodes: next } : s)),
    );
    try { navigator.vibrate?.(12); } catch (_) {}
    persistEpisodeOrder(item, next);
  };

  // Persist a new episode order to the backend, then renumber so
  // `episode_number` aligns with the new index. Two-step (reorder →
  // renumber) so the player picks up the new numbering immediately.
  const persistEpisodeOrder = async (item, orderedEpisodes) => {
    const isAudiobook = item.content_type === 'audiobook' || item.audiobook_url || item.chapters;
    const reorderUrl = isAudiobook
      ? `${BACKEND_URL}/api/audiobook/series/${item.series_id}/chapters/reorder`
      : `${BACKEND_URL}/api/cinematic/series/${item.series_id}/episodes/reorder`;
    const renumberUrl = isAudiobook
      ? `${BACKEND_URL}/api/audiobook/series/${item.series_id}/chapters/renumber`
      : `${BACKEND_URL}/api/cinematic/series/${item.series_id}/episodes/renumber`;
    const tid = toast.loading('Saving new order…');
    try {
      const items = orderedEpisodes.map((ep, i) => ({
        id: ep.episode_id || ep.chapter_id,
        order: i + 1,
      }));
      const r1 = await authFetch(reorderUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!r1.ok) throw new Error(`reorder ${r1.status}`);
      const r2 = await authFetch(renumberUrl, { method: 'POST' });
      if (!r2.ok) throw new Error(`renumber ${r2.status}`);
      toast.success('✓ Order saved', { id: tid });
      await fetchAllSeries();
    } catch (e) {
      toast.error(`Failed to reorder: ${e?.message || 'unknown'}`, { id: tid });
    }
  };

  // Move episode to a specific 1-based position (via the position
  // input column). Validates the bound and reuses the drag-drop persist.
  const handlePositionChange = (item, fromIndex, rawNext) => {
    const total = item.episodes?.length || 0;
    const targetIdx = Math.max(0, Math.min(total - 1, (parseInt(rawNext, 10) || 1) - 1));
    if (targetIdx === fromIndex) return;
    const next = [...(item.episodes || [])];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(targetIdx, 0, moved);
    setSeries((prev) =>
      prev.map((s) => (s.series_id === item.series_id ? { ...s, episodes: next } : s)),
    );
    persistEpisodeOrder(item, next);
  };

  // ↑/↓ button fail-safe — guaranteed to work on every device, even when
  // drag is blocked by a browser quirk / extension / hover overlay. Each
  // tap moves the episode one slot up or down, then persists + renumbers.
  const moveEpisodeBy = (item, fromIndex, delta) => {
    const total = item.episodes?.length || 0;
    const target = fromIndex + delta;
    if (target < 0 || target >= total) return;
    handlePositionChange(item, fromIndex, target + 1);
  };

  const handleEpisodeFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['audio/', 'video/'];
      const isValid = allowedTypes.some(t => file.type.startsWith(t));

      if (!isValid) {
        toast.error('Please select an audio or video file');
        return;
      }

      setEpisodeFile(file);
    }
  };

  const uploadEpisode = async (seriesId, contentType) => {
    if (!episodeFile) {
      toast.error('Please select a file');
      return;
    }

    if (!episodeForm.title || !episodeForm.episode_number) {
      toast.error('Please fill in title and episode number');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', episodeFile);
      if (contentType === 'audiobook') {
        formData.append('chapter_title', episodeForm.title);
        formData.append('chapter_number', episodeForm.episode_number);
      } else {
        formData.append('episode_title', episodeForm.title);
        formData.append('episode_number', episodeForm.episode_number);
      }
      // When inserting between existing episodes the scribe tapped
      // "+ Insert here", so we tell the backend to shift later
      // episodes up by 1 instead of failing on the duplicate number.
      if (shiftExisting) formData.append('shift_existing', 'true');

      const endpoint = contentType === 'audiobook'
        ? `/api/audiobook/series/${seriesId}/chapter`
        : `/api/cinematic/series/${seriesId}/episode`;

      const uploadPromise = authFetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        body: formData
      });

      toast.promise(uploadPromise, {
        loading: 'Uploading episode...',
        success: 'Episode uploaded successfully!',
        error: 'Failed to upload episode'
      });

      const response = await uploadPromise;
      
      if (!response.ok) throw new Error('Upload failed');

      cancelAddEpisode();
      fetchAllSeries();
    } catch (error) {
      console.error('Upload error:', error);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen bg-slate-900 text-white">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header */}
      <header className="bg-slate-800/50 backdrop-blur-sm border-b border-slate-700/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
              Manage All Series
              {xpTotal > 0 && (
                <span
                  className="text-xs px-2 py-1 rounded-full border border-amber-400/40 bg-amber-500/15 text-amber-200 font-bold inline-flex items-center gap-1"
                  data-testid="total-xp-pill"
                  title="Lifetime XP across every upload — visible to writer & admins only"
                >
                  <Sparkles className="w-3 h-3" /> {xpTotal.toLocaleString()} XP
                </span>
              )}
            </h1>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={() => {
                  if (selectMode) exitSelectMode();
                  else setSelectMode(true);
                }}
                variant="outline"
                size="sm"
                className={
                  selectMode
                    ? 'text-amber-200 border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/20'
                    : 'text-amber-300 border-amber-500/40 hover:bg-amber-500/10'
                }
                data-testid="bulk-select-toggle"
                title="Multi-select series to bulk delete or move between Library / Vault"
              >
                {selectMode ? (
                  <>
                    <X className="w-3.5 h-3.5 mr-1.5" /> Cancel select
                  </>
                ) : (
                  <>
                    <CheckSquare className="w-3.5 h-3.5 mr-1.5" /> Select
                  </>
                )}
              </Button>
              <Button
                onClick={runBackfill}
                disabled={backfilling}
                variant="outline"
                size="sm"
                className="text-indigo-300 border-indigo-500/40 hover:bg-indigo-600/20"
                data-testid="backfill-durations-btn"
                title="Re-read every audio/video file on disk and persist its true length. Safe to re-run."
              >
                <Clock className="w-3.5 h-3.5 mr-1.5" />
                {backfilling ? 'Backfilling...' : 'Backfill durations'}
              </Button>
              <Button onClick={() => navigate('/dashboard')} variant="outline" size="sm" className="flex-shrink-0">
                <span className="hidden sm:inline">Back to Dashboard</span>
                <span className="sm:hidden">Back</span>
              </Button>
            </div>
          </div>
        </div>
        {/* Sticky bulk-action bar — appears below the page header so it
            scrolls along with the rest of the sticky header. Hidden when
            the user isn't in select mode or hasn't picked anything yet. */}
        {selectMode && selectedIds.size > 0 && (
          <div
            className="border-t border-amber-500/30 bg-amber-500/5"
            data-testid="bulk-action-bar"
          >
            <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-2 flex-wrap">
              <span className="text-sm font-semibold text-amber-200">
                {selectedIds.size} selected
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => runBulkAction('vault')}
                  disabled={bulkBusy}
                  className="text-fuchsia-200 border-fuchsia-500/40 hover:bg-fuchsia-500/10"
                  data-testid="bulk-action-move-vault"
                >
                  <Lock className="w-3.5 h-3.5 mr-1.5" /> Move to Vault
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => runBulkAction('library')}
                  disabled={bulkBusy}
                  className="text-emerald-200 border-emerald-500/40 hover:bg-emerald-500/10"
                  data-testid="bulk-action-move-library"
                >
                  <Unlock className="w-3.5 h-3.5 mr-1.5" /> Move to Library
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => runBulkAction('delete')}
                  disabled={bulkBusy}
                  className="text-red-300 border-red-500/40 hover:bg-red-500/10"
                  data-testid="bulk-action-delete"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Series List */}
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {series.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            No series found. Upload some content to get started.
          </div>
        ) : (
          <DndContext
            sensors={dndSensors}
            collisionDetection={closestCenter}
            onDragEnd={onSeriesDragEnd}
          >
            <SortableContext
              items={series.map((s) => s.series_id)}
              strategy={verticalListSortingStrategy}
            >
              {series.map((item, index) => (
                <SortableSeriesRow
                  key={item.series_id}
                  id={item.series_id}
                  selected={selectMode && selectedIds.has(item.series_id)}
                >
                  {({ listeners: dragListeners }) => (
                    <>
              {/* Series Header */}
              <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 flex-wrap">
                {/* Bulk-select checkbox — only visible while the admin is
                    in select mode, so the page stays uncluttered the rest
                    of the time. */}
                {selectMode && (
                  <button
                    type="button"
                    onClick={() => toggleSelected(item.series_id)}
                    className="flex-shrink-0 text-amber-300 hover:text-amber-200"
                    title={selectedIds.has(item.series_id) ? 'Deselect' : 'Select'}
                    data-testid={`bulk-select-row-${item.series_id}`}
                  >
                    {selectedIds.has(item.series_id) ? (
                      <CheckSquare className="w-5 h-5" />
                    ) : (
                      <Square className="w-5 h-5" />
                    )}
                  </button>
                )}
                {/* Reorder Controls — ↑/↓ buttons + drag-grip handle.
                    Drag listeners are bound only to the GripVertical so
                    nested buttons (edit, delete, expand) stay tappable. */}
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveUp(index)}
                    disabled={index === 0}
                    className="h-6 w-6 p-0"
                    data-testid={`series-move-up-${item.series_id}`}
                  >
                    <ChevronUp className="w-4 h-4" />
                  </Button>
                  <button
                    type="button"
                    {...dragListeners}
                    className="w-6 h-6 rounded text-gray-500 hover:text-indigo-300 active:text-indigo-200 cursor-grab active:cursor-grabbing touch-none select-none flex items-center justify-center"
                    aria-label="Drag to reorder series"
                    title="Drag to reorder"
                    data-testid={`series-drag-handle-${item.series_id}`}
                  >
                    <GripVertical className="w-5 h-5" />
                  </button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveDown(index)}
                    disabled={index === series.length - 1}
                    className="h-6 w-6 p-0"
                    data-testid={`series-move-down-${item.series_id}`}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </div>

                {/* Order Badge */}
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-indigo-600/20 text-indigo-400 font-semibold flex-shrink-0">
                  {index + 1}
                </div>

                {/* Cover Image */}
                <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center flex-shrink-0 group/cover">
                  {item.cover_image ? (
                    <img
                      src={resolveCoverUrl(item.cover_image)}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="text-2xl">🎬</span>
                  )}
                  {item.cover_image && (item.type === 'audiobook' || item.type === 'cinematic') && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); generateAiCover(item, true); }}
                      disabled={aiCoverBusyId === item.series_id}
                      className="absolute bottom-0.5 right-0.5 w-6 h-6 rounded-full bg-fuchsia-600/90 hover:bg-fuchsia-500 text-white flex items-center justify-center shadow-lg shadow-black/40 ring-1 ring-white/20 sm:opacity-0 sm:group-hover/cover:opacity-100 transition-opacity disabled:opacity-100 disabled:cursor-wait"
                      data-testid={`regenerate-cover-btn-${item.series_id}`}
                      title="Regenerate AI cover with Nano Banana"
                      aria-label="Regenerate AI cover"
                    >
                      <Sparkles className={`w-3 h-3 ${aiCoverBusyId === item.series_id ? 'animate-pulse' : ''}`} />
                    </button>
                  )}
                </div>

                {/* Series Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold break-words">{item.title}</h3>
                  <p className="text-sm text-gray-400 truncate">by {item.author}</p>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    <span className="text-xs px-2 py-1 bg-slate-700 rounded">{item.genre}</span>
                    <span className="text-xs px-2 py-1 bg-slate-700 rounded">{item.type}</span>
                    <span className="text-xs px-2 py-1 bg-slate-700 rounded">
                      {item.episodes?.length || 0} episodes
                    </span>
                    {(() => {
                      // Sum the XP earned across every episode in this
                      // series. Visible only to admin + the scribe who
                      // owns the series (the endpoint enforces that).
                      const sum = (item.episodes || []).reduce(
                        (acc, ep) => acc + (xpMap[ep.episode_id || ep.chapter_id] || 0),
                        0,
                      );
                      if (sum <= 0) return null;
                      return (
                        <span
                          className="text-xs px-2 py-1 rounded font-bold border border-amber-400/40 bg-amber-500/15 text-amber-200 inline-flex items-center gap-1"
                          title="Total XP rewarded across this series — visible to writer & admins only"
                          data-testid={`series-xp-${item.series_id}`}
                        >
                          <Sparkles className="w-3 h-3" /> {sum.toLocaleString()} XP
                        </span>
                      );
                    })()}
                    {item.difficulty && (
                      <span
                        className={`text-xs px-2 py-1 rounded font-bold border ${
                          item.difficulty === 'Beginner'     ? 'border-emerald-400/50 text-emerald-200 bg-emerald-500/20' :
                          item.difficulty === 'Intermediate' ? 'border-amber-400/50 text-amber-200 bg-amber-500/20' :
                                                               'border-rose-400/50 text-rose-200 bg-rose-500/20'
                        }`}
                        data-testid={`difficulty-badge-${item.series_id}`}
                      >
                        {item.difficulty}
                      </span>
                    )}
                    {!item.cover_image && (
                      <>
                        <button
                          type="button"
                          onClick={() => startEdit(item)}
                          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold px-2 py-1 rounded bg-amber-500/15 text-amber-200 border border-amber-500/40 hover:bg-amber-500/25 transition-colors"
                          data-testid={`missing-cover-pill-${item.series_id}`}
                          title="Click to add a cover image for this series"
                        >
                          <ImageIcon className="w-3 h-3" />
                          Missing cover · Add
                        </button>
                        <button
                          type="button"
                          onClick={() => generateAiCover(item)}
                          disabled={aiCoverBusyId === item.series_id}
                          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold px-2 py-1 rounded bg-fuchsia-500/15 text-fuchsia-200 border border-fuchsia-500/40 hover:bg-fuchsia-500/25 transition-colors disabled:opacity-60 disabled:cursor-wait"
                          data-testid={`ai-cover-pill-${item.series_id}`}
                          title="Auto-generate a cover with Gemini Nano Banana"
                        >
                          <Sparkles className="w-3 h-3" />
                          {aiCoverBusyId === item.series_id ? 'Generating...' : '✨ AI cover'}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-1 sm:gap-2 flex-shrink-0 ml-auto">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/improve?ref_id=${encodeURIComponent(item.series_id)}`)}
                    className="text-indigo-300 hover:text-indigo-200"
                    title="Open Improve analytics"
                    data-testid={`series-improve-${item.series_id}`}
                  >
                    <BarChart3 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleExpand(item.series_id)}
                  >
                    {expandedSeries[item.series_id] ? <ChevronUp /> : <ChevronDown />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => startEdit(item)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteSeries(item.series_id, item.title, item.type)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Expanded Edit Form */}
              {editingId === item.series_id && (
                <div className="border-t border-slate-700 p-6 bg-slate-900/50 space-y-4">
                  <h4 className="text-lg font-semibold mb-4">Edit Series</h4>
                  
                  {/* Cover Image Upload */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Cover Image</label>
                    <div className="flex gap-4 items-start">
                      {coverPreview && (
                        <img src={coverPreview} alt="Preview" className="w-32 h-32 object-cover rounded-lg" />
                      )}
                      <div className="flex-1 space-y-3">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleCoverChange}
                          className="hidden"
                          id={`cover-${item.series_id}`}
                        />
                        <label
                          htmlFor={`cover-${item.series_id}`}
                          className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                        >
                          <Upload className="w-4 h-4" />
                          Choose Image
                        </label>
                        {/* Apply-to-all toggle. Only meaningful for series
                            (cinematic / audiobook). On a fresh edit we default
                            to ON because that's the most common intent — when
                            you're updating album art, you almost always want
                            every episode to match. */}
                        {(item.type === 'cinematic' || item.type === 'audiobook') && (
                          <label
                            className="flex items-center gap-2 text-sm text-gray-200 cursor-pointer select-none bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2 hover:border-emerald-500/40 transition-colors"
                            data-testid={`apply-cover-to-all-toggle-${item.series_id}`}
                          >
                            <input
                              type="checkbox"
                              checked={applyCoverToAllEpisodes}
                              onChange={(e) => setApplyCoverToAllEpisodes(e.target.checked)}
                              className="accent-emerald-500 w-4 h-4"
                            />
                            <Layers className="w-4 h-4 text-emerald-400" />
                            <span>
                              Apply this cover to <strong>all {item.episodes?.length || 0} episodes</strong>
                              <span className="block text-[11px] text-gray-400 leading-tight">
                                Existing + any new ones you upload later
                              </span>
                            </span>
                          </label>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Form Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Title</label>
                      <Input
                        value={editForm.title || ''}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Author</label>
                      <Input
                        value={editForm.author || ''}
                        onChange={(e) => setEditForm({ ...editForm, author: e.target.value })}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Genre</label>
                      <Input
                        value={editForm.genre || ''}
                        onChange={(e) => setEditForm({ ...editForm, genre: e.target.value })}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Description</label>
                    <textarea
                      value={editForm.description || ''}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 min-h-[100px]"
                    />
                  </div>

                  {/* 💰 Price — inline editor that mirrors the upload form
                      (PriceTagEditor). Writers can change the price of an
                      already-published book/series without re-uploading.
                      Server enforces the £15 cap unless admin flipped
                      "no_max". Feb 22 2026. */}
                  <div data-testid={`price-row-${item.series_id || item.content_id || item.book_id}`}>
                    <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                      Price
                      <span className="text-[10px] uppercase tracking-wider font-bold text-amber-300">
                        Library item
                      </span>
                    </label>
                    <PriceTagEditor
                      contentId={item.series_id || item.content_id || item.book_id}
                      value={item.price_coins ?? null}
                      noMax={!!item.no_max_price}
                      onSaved={(coins, noMax) => {
                        // Refresh the row in place so the next render shows
                        // the new price without a full reload.
                        item.price_coins = coins;
                        item.no_max_price = noMax;
                      }}
                      testIdPrefix={`manage-price-${item.series_id || item.content_id || item.book_id}`}
                    />
                  </div>

                  {/* Difficulty tag — Beginner / Intermediate / Advanced.
                      Stored on the series doc and surfaced as a badge on
                      the scribe/admin view. "None" clears the tag. */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Difficulty</label>
                    <div className="flex flex-wrap gap-2" data-testid={`difficulty-row-${item.series_id}`}>
                      {['', 'Beginner', 'Intermediate', 'Advanced'].map((lvl) => {
                        const isActive = (editForm.difficulty || '') === lvl;
                        const label = lvl || 'None';
                        const tone =
                          lvl === 'Beginner'     ? 'border-emerald-400/50 text-emerald-200 bg-emerald-500/20' :
                          lvl === 'Intermediate' ? 'border-amber-400/50 text-amber-200 bg-amber-500/20' :
                          lvl === 'Advanced'     ? 'border-rose-400/50 text-rose-200 bg-rose-500/20' :
                                                   'border-slate-500 text-slate-200 bg-slate-700/40';
                        return (
                          <button
                            key={label}
                            type="button"
                            onClick={() => setEditForm({ ...editForm, difficulty: lvl })}
                            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                              isActive
                                ? tone
                                : 'border-slate-700 text-gray-400 hover:border-slate-500 bg-slate-800/40'
                            }`}
                            data-testid={`difficulty-${lvl || 'none'}-${item.series_id}`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Save/Cancel Buttons */}
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={cancelEdit}>
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                    <Button onClick={() => saveEdit(item.series_id, item.type)}>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </Button>
                  </div>
                </div>
              )}

              {/* Expanded Episodes List */}
              {expandedSeries[item.series_id] && !editingId && (
                <div className="border-t border-slate-700 p-4 bg-slate-900/30">
                  <div className="flex items-center justify-between mb-3 gap-2">
                    <h4 className="text-sm font-semibold">Episodes</h4>
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Renumber 1→N — closes any gaps in the sequence
                          and re-assigns sequential numbers based on the
                          current order. Tiny, deterministic, undo-able
                          with a redo (drag → renumber → done). */}
                      {item.episodes && item.episodes.length > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRenumber(item)}
                          disabled={renumberingId === item.series_id}
                          className="border-amber-500/60 text-amber-300 hover:bg-amber-500/20"
                          data-testid={`renumber-episodes-${item.series_id}`}
                          title="Re-assign episode numbers as 1, 2, 3… (closes gaps)"
                        >
                          {renumberingId === item.series_id ? (
                            <span className="text-xs">Renumbering…</span>
                          ) : (
                            <>
                              <ListOrdered className="w-4 h-4 mr-2" />
                              Renumber 1→{item.episodes.length}
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        onClick={() => startAddEpisode(item.series_id)}
                        className="bg-indigo-600 hover:bg-indigo-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Episode
                      </Button>
                    </div>
                  </div>

                  {/* Add Episode Form */}
                  {uploadingEpisode === item.series_id && (
                    <div className="mb-4 p-4 bg-slate-800/50 rounded-lg border border-indigo-500/30 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <h5 className="font-medium text-indigo-400">
                          {shiftExisting
                            ? `Insert at #${episodeForm.episode_number} (shifts later episodes up)`
                            : 'Upload New Episode'}
                        </h5>
                        {shiftExisting && (
                          <span className="text-[10px] uppercase tracking-widest font-bold bg-indigo-600/30 text-indigo-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                            Insert mode
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1">Episode Title *</label>
                          <Input
                            value={episodeForm.title}
                            onChange={(e) => setEpisodeForm({ ...episodeForm, title: e.target.value })}
                            placeholder="Episode 1: The Beginning"
                            className="bg-slate-900 border-slate-700"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Episode Number *</label>
                          <Input
                            type="number"
                            value={episodeForm.episode_number}
                            onChange={(e) => setEpisodeForm({ ...episodeForm, episode_number: e.target.value })}
                            placeholder="1"
                            className="bg-slate-900 border-slate-700"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium mb-1">Description</label>
                        <textarea
                          value={episodeForm.description}
                          onChange={(e) => setEpisodeForm({ ...episodeForm, description: e.target.value })}
                          placeholder="Episode description..."
                          className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-sm min-h-[60px]"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium mb-1">
                          {item.type === 'audiobook' ? 'Audio File *' : 'Video File *'}
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="file"
                            accept={item.type === 'audiobook' ? 'audio/*' : 'video/*,audio/*'}
                            onChange={handleEpisodeFileChange}
                            className="hidden"
                            id={`episode-file-${item.series_id}`}
                          />
                          <label
                            htmlFor={`episode-file-${item.series_id}`}
                            className="cursor-pointer flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors text-sm"
                          >
                            <Upload className="w-4 h-4" />
                            {episodeFile ? episodeFile.name : 'Choose File'}
                          </label>
                        </div>
                        {episodeFile && (
                          <div className="text-xs text-gray-400 mt-1">
                            Size: {(episodeFile.size / 1024 / 1024).toFixed(2)} MB
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" size="sm" onClick={cancelAddEpisode}>
                          Cancel
                        </Button>
                        <Button size="sm" onClick={() => uploadEpisode(item.series_id, item.type)}>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Episode
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Episodes List — wrapped in a DndContext per series
                      so the dragger only cares about THIS series' rows.
                      The "+ Insert here" rows live outside the sortable
                      ItemId set so they never become drop targets. */}
                  <div className="space-y-2">
                    {/* Column-header row — makes the editable Position input
                        unmissable. Mobile + desktop. */}
                    {item.episodes && item.episodes.length > 0 && (
                      <div className="flex items-center gap-2 sm:gap-3 px-3 pb-1 text-[10px] uppercase tracking-widest font-bold text-gray-500 select-none">
                        <span className="w-9" aria-hidden />
                        <span className="w-12 sm:w-14 text-center text-indigo-300">Position</span>
                        <span className="w-9 sm:w-8 text-center">Move</span>
                        <span className="flex-1">Episode</span>
                      </div>
                    )}
                    {item.episodes && item.episodes.length > 0 ? (
                      <DndContext
                        sensors={dndSensors}
                        collisionDetection={closestCenter}
                        onDragEnd={onEpisodeDragEnd(item)}
                      >
                        <SortableContext
                          items={item.episodes.map((ep, i) => ep.episode_id || ep.chapter_id || String(i))}
                          strategy={verticalListSortingStrategy}
                        >
                          {item.episodes.map((ep, i) => {
                            const num = Number(ep.episode_number ?? ep.chapter_number ?? i + 1);
                            const insertAt = num;
                            return (
                              <React.Fragment key={ep.episode_id || i}>
                                {/* "+ Insert here" tappable row above each episode */}
                                <button
                                  type="button"
                                  onClick={() => startAddEpisode(item.series_id, insertAt)}
                                  className="w-full group flex items-center gap-2 py-2 px-3 my-1 rounded-lg border-2 border-dashed border-indigo-500/40 hover:border-indigo-400 hover:bg-indigo-500/10 transition-colors text-indigo-300 hover:text-indigo-100"
                                  data-testid={`insert-episode-before-${ep.episode_id || i}`}
                                  title={`Insert a new episode at #${insertAt} (later episodes shift up)`}
                                >
                                  <Plus className="w-4 h-4 flex-shrink-0" />
                                  <span className="text-xs font-bold uppercase tracking-widest">
                                    Insert at #{insertAt}
                                  </span>
                                  <span className="ml-auto text-[10px] text-indigo-400/70 hidden sm:inline">
                                    shifts later episodes up
                                  </span>
                                </button>
                                <SortableEpisodeRow
                                  ep={ep}
                                  i={i}
                                  item={item}
                                  num={num}
                                  xp={xpMap[ep.episode_id || ep.chapter_id]}
                                  onPositionChange={handlePositionChange}
                                  onMoveBy={moveEpisodeBy}
                                  onEditEpisode={editEpisodeTitle}
                                  onDeleteEpisode={deleteEpisode}
                                />
                              </React.Fragment>
                            );
                          })}
                        </SortableContext>
                      </DndContext>
                    ) : (
                      <div className="text-center py-4 text-gray-500 text-sm">
                        No episodes yet. Click "Add Episode" to upload one.
                      </div>
                    )}
                    {/* Tail "+ Add at end" — clear primary CTA matching
                        the in-line Insert buttons. */}
                    {item.episodes && item.episodes.length > 0 && (
                      <button
                        type="button"
                        onClick={() => startAddEpisode(item.series_id)}
                        className="w-full group flex items-center justify-center gap-2 py-2 px-3 my-1 rounded-lg border-2 border-dashed border-emerald-500/40 hover:border-emerald-400 hover:bg-emerald-500/10 transition-colors text-emerald-300 hover:text-emerald-100"
                        data-testid={`insert-episode-tail-${item.series_id}`}
                        title="Append a new episode at the end"
                      >
                        <Plus className="w-4 h-4" />
                        <span className="text-xs font-bold uppercase tracking-widest">
                          Add new episode at end (#{((item.episodes[item.episodes.length - 1]?.episode_number || item.episodes[item.episodes.length - 1]?.chapter_number || item.episodes.length) + 1)})
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              )}
                    </>
                  )}
                </SortableSeriesRow>
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
};

export default ManageContent;
